import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Breadcrumbs,
  Button, 
  Container, 
  Grid, 
  Link,
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  IconButton,
  Tooltip,
  InputAdornment,
  alpha,
  Paper,
  Divider,
  Tabs,
  Tab
} from '@material-ui/core';
import { motion, AnimatePresence } from 'framer-motion';
import PlusIcon from '../../icons/Plus';
import PencilAltIcon from '../../icons/PencilAlt';
import TrashIcon from '../../icons/Trash';
import EyeIcon from '../../icons/Eye';
import ClipboardListIcon from '../../icons/ClipboardList';
import ChevronRightIcon from '../../icons/ChevronRight';
import PaperAirplaneIcon from '../../icons/PaperAirplane';
import DuplicateIcon from '../../icons/Duplicate';
import SearchIcon from '@material-ui/icons/Search';
import QuestionnairePreview from '../../components/dashboard/questionnaire/QuestionnairePreview';
import DescriptionIcon from '@mui/icons-material/Description'; // Forcé pour rafraîchir le dev server
import { formationApi } from '../../api/formationApi';
import { questionnaireApi } from '../../api/questionnaireApi';
import useMounted from '../../hooks/useMounted';
import useSettings from '../../hooks/useSettings';
import toast from 'react-hot-toast';
import Scrollbar from '../../components/Scrollbar';

// --- EXTRAORDINARY STYLING COMPONENTS ---

const AnimatedMeshBackground = () => (
    <Box sx={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
        overflow: 'hidden', zIndex: 0, opacity: 0.4,
        background: '#F8FAFC',
        pointerEvents: 'none'
    }}>
        <motion.div
            animate={{ x: [0, 120, -60, 0], y: [0, -120, 60, 0], scale: [1, 1.3, 0.8, 1] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', top: '-15%', left: '-10%', width: '45vw', height: '45vw', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(60px)' }}
        />
        <motion.div
            animate={{ x: [0, -100, 80, 0], y: [0, 100, -50, 0], scale: [1, 1.2, 0.9, 1] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', bottom: '-25%', right: '-10%', width: '55vw', height: '55vw', background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(60px)' }}
        />
    </Box>
);

const QuestionnaireFormation = () => {
  const { settings } = useSettings();
  const [openModal, setOpenModal] = useState(false);
  const [formations, setFormations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  
  const [nom, setNom] = useState('');
  const [type, setType] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [formatFilter, setFormatFilter] = useState('');
  const [professionFilter, setProfessionFilter] = useState('');

  // State pour l'envoi contextuel (Global)
  const [openSendModal, setOpenSendModal] = useState(false);
  const [sendingQuestionnaireId, setSendingQuestionnaireId] = useState(null);
  const [sendFormation, setSendFormation] = useState('');
  const [sendSession, setSendSession] = useState('');
  const [sendSessions, setSendSessions] = useState([]);

  // State pour le preview
  const [openPreview, setOpenPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // State pour l'édition en ligne
  const [editingCell, setEditingCell] = useState(null); 
  const [editValue, setEditValue] = useState('');
  const [editSessions, setEditSessions] = useState([]);
  const [loadingEditSessions, setLoadingEditSessions] = useState(false);
  
  const mounted = useMounted();
  const navigate = useNavigate();

  const getFormations = useCallback(async () => {
    try {
      const data = await formationApi.getFormations(localStorage.getItem('accessToken'));
      if (mounted.current) setFormations(data);
    } catch (err) { console.error(err); }
  }, [mounted]);

  const getQuestionnaires = useCallback(async () => {
    try {
      const response = await questionnaireApi.getQuestionnaires(localStorage.getItem('accessToken'));
      if (mounted.current && response && response.status === 200) {
        setQuestionnaires(response.data);
      }
    } catch (err) { console.error(err); }
  }, [mounted]);

  useEffect(() => {
    getFormations();
    getQuestionnaires();
  }, [getFormations, getQuestionnaires]);

  useEffect(() => {
    const fetchSessions = async () => {
      if (!selectedFormation) {
        setSessions([]);
        setSelectedSession('');
        return;
      }
      try {
        const data = await questionnaireApi.getSessionsByFormationId(selectedFormation, localStorage.getItem('accessToken'));
        if (mounted.current) {
          setSessions(data || []);
          setSelectedSession('');
        }
      } catch (err) { console.error(err); }
    };
    fetchSessions();
  }, [selectedFormation, mounted]);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => {
    setOpenModal(false);
    setNom('');
    setType('');
    setIsGlobal(false);
    setSelectedFormation('');
    setSelectedSession('');
  };

  const handleCreate = async () => {
    if (!nom || !type) {
      toast.error('Veuillez remplir le nom et le type du questionnaire.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: nom,
        type: type,
        formation_id: isGlobal ? null : (selectedFormation || null),
        session_id: isGlobal ? null : (selectedSession || null)
      };
      const response = await questionnaireApi.createQuestionnaire(payload, localStorage.getItem('accessToken'));
      if (response && response.status === 200) {
        toast.success('Questionnaire créé avec succès !');
        handleClose();
        getQuestionnaires();
      } else {
        toast.error(response.message || 'Une erreur est survenue.');
      }
    } catch (error) {
      toast.error('Erreur lors de la création.');
    } finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if(!window.confirm("Êtes-vous sûr de vouloir supprimer ce questionnaire ?")) return;
    try {
      const response = await questionnaireApi.deleteQuestionnaire(id, localStorage.getItem('accessToken'));
      if(response && response.status === 200) {
        toast.success('Questionnaire supprimé.');
        getQuestionnaires();
      }
    } catch(err) { toast.error('Erreur inattendue.'); }
  };
  
  const handleOpenPreview = async (questionnaire) => {
    setPreviewData(questionnaire);
    setOpenPreview(true);
    setLoadingPreview(true);
    try {
      const accessToken = window.localStorage.getItem('accessToken');
      const response = await questionnaireApi.getQuestions(questionnaire.id, accessToken);
      if (response.status) setPreviewQuestions(response.data);
    } catch (err) { toast.error('Erreur lors du chargement'); }
    finally { setLoadingPreview(false); }
  };

  const handleOpenSendModal = async (qId) => {
    setSendingQuestionnaireId(qId);
    setSendFormation('');
    setSendSession('');
    setSendSessions([]);
    setOpenSendModal(true);
  };

  const handleSendEmail = async (id, contextFormationId = null, contextSessionId = null) => {
    const accessToken = window.localStorage.getItem('accessToken');
    const loadToast = toast.loading('Envoi en cours...');
    try {
      const response = await questionnaireApi.sendByEmail(id, accessToken, contextFormationId, contextSessionId);
      if (response.status) {
        toast.success('Questionnaire envoyé !', { id: loadToast });
        setOpenSendModal(false);
      } else {
        toast.error('Erreur lors de l\'envoi.', { id: loadToast });
      }
    } catch (err) { toast.error('Erreur technique.', { id: loadToast }); }
  };

  const handleDuplicate = async (id) => {
    const accessToken = window.localStorage.getItem('accessToken');
    const loadToast = toast.loading('Duplication en cours...');
    try {
      const response = await questionnaireApi.duplicateQuestionnaire(id, accessToken);
      if (response.status) {
        toast.success('Dupliqué !', { id: loadToast });
        getQuestionnaires();
      }
    } catch (err) { toast.error('Erreur technique.', { id: loadToast }); }
  };

  const handleEditStart = async (q, field) => {
    setEditingCell({ id: q.id, field });
    if (field === 'session_id') {
      setEditValue(q.session_id || '');
      // Charger les sessions de la formation associée
      if (q.formation_id) {
        setLoadingEditSessions(true);
        try {
          const data = await questionnaireApi.getSessionsByFormationId(q.formation_id, localStorage.getItem('accessToken'));
          setEditSessions(data || []);
        } catch (err) { setEditSessions([]); }
        finally { setLoadingEditSessions(false); }
      } else {
        setEditSessions([]);
      }
    } else {
      setEditValue(q[field] || '');
    }
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
    setEditSessions([]);
  };

  const handleEditSave = async (q, field, forceValue = undefined) => {
    const valueToSave = forceValue !== undefined ? forceValue : editValue;
    const currentValue = field === 'session_id' ? (q.session_id || '') : (q[field] || '');
    if (valueToSave === currentValue) {
      handleEditCancel();
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const payload = { ...q, [field]: valueToSave || null };
      const response = await questionnaireApi.updateQuestionnaire(payload, accessToken);
      if (response && response.status === 200) {
        toast.success('Mis à jour');
        getQuestionnaires();
      }
    } catch (err) { toast.error('Erreur technique'); }
    finally { handleEditCancel(); }
  };

  // Correspondance formation_id -> formation (pour récupérer format / professions)
  const formationById = useMemo(() => {
    const map = {};
    formations.forEach((f) => { map[String(f.id)] = f; });
    return map;
  }, [formations]);

  // Options de filtres dérivées des formations liées aux questionnaires
  const formatOptions = useMemo(() => {
    const set = new Set();
    questionnaires.forEach((q) => {
      const f = q.formation_id ? formationById[String(q.formation_id)] : null;
      if (f && f.format && f.format.trim()) set.add(f.format.trim());
    });
    return Array.from(set).sort();
  }, [questionnaires, formationById]);

  const professionOptions = useMemo(() => {
    const set = new Set();
    questionnaires.forEach((q) => {
      const f = q.formation_id ? formationById[String(q.formation_id)] : null;
      if (f && f.professions) {
        f.professions.split(',').forEach((p) => {
          const trimmed = p.trim();
          if (trimmed) set.add(trimmed);
        });
      }
    });
    return Array.from(set).sort();
  }, [questionnaires, formationById]);

  const filteredQuestionnaires = questionnaires.filter((q) => {
    const matchesTab = activeTab === 'All' || q.type === activeTab;
    const matchesSearch = q.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (q.formation_name && q.formation_name.toLowerCase().includes(searchQuery.toLowerCase()));

    const formation = q.formation_id ? formationById[String(q.formation_id)] : null;

    // Filtre Format : les questionnaires globaux (sans formation) passent toujours
    let matchesFormat = true;
    if (formatFilter) {
      matchesFormat = formation ? (formation.format || '') === formatFilter : true;
    }

    // Filtre Profession : la formation peut avoir plusieurs professions (séparées par virgules)
    let matchesProfession = true;
    if (professionFilter) {
      if (formation && formation.professions) {
        const profs = formation.professions.split(',').map((p) => p.trim());
        matchesProfession = profs.includes(professionFilter);
      } else {
        matchesProfession = !formation; // globaux passent, formations sans profession non
      }
    }

    return matchesTab && matchesSearch && matchesFormat && matchesProfession;
  });

  const stats = {
    total: questionnaires.length,
    formations: [...new Set(questionnaires.filter(q => q.formation_id).map(q => q.formation_id))].length,
    sessions: [...new Set(questionnaires.filter(q => q.session_id).map(q => q.session_id))].length
  };

  return (
    <>
      <Helmet>
        <title>Qualité | Questionnaires | Apteed</title>
      </Helmet>
      
      <Box sx={{ 
          bgcolor: '#F8FAFC', 
          minHeight: '100%', 
          position: 'relative', 
          overflow: 'hidden',
          pb: 10,
          pt: 4
      }}>
        <AnimatedMeshBackground />

        <Container maxWidth={settings.compact ? 'xl' : false} sx={{ position: 'relative', zIndex: 1 }}>
          
          {/* Header Bento Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                  p: 3, borderRadius: '24px', 
                  background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionIcon sx={{ color: '#fff', fontSize: '1.7rem' }} />
                  </Box>
                  <Box>
                    <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
                        <Link color="textPrimary" component={RouterLink} to="/dashboard" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: '#6366f1' } }}>Dashboard</Link>
                        <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Qualité</Typography>
                    </Breadcrumbs>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#0F172A', letterSpacing: '-0.02em', mb: 0.5 }}>
                        Gestion des Questionnaires
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                        Créez et administrez vos questionnaires de formation avec précision.
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        sx={{ maxWidth: 350, flexGrow: 1 }}
                        size="small"
                        placeholder="Rechercher par nom ou formation..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#6366F1', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '14px', bgcolor: 'white' }
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Format"
                        value={formatFilter}
                        onChange={(e) => setFormatFilter(e.target.value)}
                        variant="outlined"
                        sx={{ minWidth: 170 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ sx: { borderRadius: '14px', bgcolor: 'white' } }}
                    >
                        <MenuItem value="">Tous les formats</MenuItem>
                        {formatOptions.map((f) => ( <MenuItem key={f} value={f}>{f}</MenuItem> ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Profession"
                        value={professionFilter}
                        onChange={(e) => setProfessionFilter(e.target.value)}
                        variant="outlined"
                        sx={{ minWidth: 180 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ sx: { borderRadius: '14px', bgcolor: 'white' } }}
                    >
                        <MenuItem value="">Toutes les professions</MenuItem>
                        {professionOptions.map((p) => ( <MenuItem key={p} value={p}>{p}</MenuItem> ))}
                    </TextField>
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<PlusIcon fontSize="small" />}
                        onClick={handleOpen}
                        sx={{
                          borderRadius: '14px',
                          px: 3,
                          py: 1,
                          fontWeight: 800,
                          boxShadow: '0 10px 20px -5px rgba(99,102,241,0.4)',
                          textTransform: 'none'
                        }}
                      >
                        Créer un questionnaire
                    </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
                <Paper sx={{ 
                    p: 3, borderRadius: '24px', height: '100%',
                    background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                    color: '#FFFFFF', position: 'relative', overflow: 'hidden',
                    boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.2)',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center'
                }}>
                    <Box sx={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, rgba(0,0,0,0) 70%)', borderRadius: '50%' }} />
                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>{stats.total}</Typography>
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Questionnaires</Typography>
                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{stats.formations}</Typography>
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.65rem' }}>Formations liées</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{stats.sessions}</Typography>
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.65rem' }}>Sessions liées</Typography>
                        </Box>
                    </Box>
                </Paper>
            </Grid>
          </Grid>

          {/* Extraordinary Filter Tabs */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Paper sx={{ 
                p: 0.5, borderRadius: '20px', bgcolor: 'rgba(255,255,255,0.6)', 
                backdropFilter: 'blur(10px)', border: '1px solid #FFFFFF',
                boxShadow: '0 4px 10px rgba(0,0,0,0.02)'
            }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, val) => setActiveTab(val)}
                    TabIndicatorProps={{ sx: { display: 'none' } }}
                    sx={{ 
                        '& .MuiTabs-flexContainer': { gap: 1 },
                        minHeight: 'auto'
                    }}
                >
                    {[
                        { label: 'Tous', value: 'All' },
                        { label: 'Pré-Formation', value: 'Pré-Formation' },
                        { label: 'Post-Formation', value: 'Post-Formation' },
                        { label: 'Satisfaction', value: 'Satisfaction' }
                    ].map((tab) => (
                        <Tab 
                            key={tab.value}
                            label={tab.label} 
                            value={tab.value}
                            sx={{ 
                                minHeight: 'auto',
                                py: 1, px: 3,
                                borderRadius: '16px',
                                fontWeight: 800,
                                fontSize: '0.8rem',
                                color: '#64748B',
                                textTransform: 'none',
                                transition: 'all 0.3s',
                                '&.Mui-selected': {
                                    bgcolor: '#FFFFFF',
                                    color: '#6366F1',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                }
                            }}
                        />
                    ))}
                </Tabs>
            </Paper>
          </Box>

          {/* Table Section */}
          <Card sx={{ 
              borderRadius: '24px', 
              border: '1px solid rgba(255,255,255,0.9)', 
              background: 'rgba(255,255,255,0.7)', 
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
              overflow: 'hidden'
          }}>
            <Scrollbar>
              <Box sx={{ minWidth: 900 }}>
                <Table>
                  <TableHead sx={{ bgcolor: 'rgba(248, 250, 252, 0.5)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' }}>Nom du questionnaire</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' }}>Formation / Cible</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' }}>Session</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence mode="popLayout">
                        {filteredQuestionnaires.map((q) => (
                            <TableRow 
                                component={motion.tr}
                                layout
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                hover 
                                key={q.id}
                                sx={{ '&:hover': { bgcolor: 'rgba(99,102,241,0.02) !important' } }}
                            >
                            <TableCell onDoubleClick={() => handleEditStart(q, 'name')}>
                                {editingCell?.id === q.id && editingCell?.field === 'name' ? (
                                <TextField
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleEditSave(q, 'name')}
                                    onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleEditSave(q, 'name');
                                    if (e.key === 'Escape') handleEditCancel();
                                    }}
                                    variant="standard"
                                />
                                ) : (
                                <Typography sx={{ fontWeight: 800, color: '#1E293B', fontSize: '0.9rem' }}>
                                    {q.name}
                                </Typography>
                                )}
                            </TableCell>
                            <TableCell onDoubleClick={() => handleEditStart(q, 'type')} sx={{ cursor: 'pointer' }}>
                                {editingCell?.id === q.id && editingCell?.field === 'type' ? (
                                <TextField
                                    select
                                    autoFocus
                                    size="small"
                                    value={editValue}
                                    onChange={(e) => {
                                      setEditValue(e.target.value);
                                      handleEditSave(q, 'type', e.target.value);
                                    }}
                                    onBlur={() => handleEditSave(q, 'type')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') handleEditCancel();
                                    }}
                                    variant="standard"
                                    sx={{ minWidth: 140 }}
                                    SelectProps={{ native: true }}
                                >
                                    <option value="Pré-Formation">Pré-Formation</option>
                                    <option value="Post-Formation">Post-Formation</option>
                                    <option value="Satisfaction">Satisfaction</option>
                                </TextField>
                                ) : (
                                <Tooltip title="Double-cliquez pour modifier" placement="top" arrow>
                                <Box sx={{ 
                                    display: 'inline-flex', px: 1.5, py: 0.5, borderRadius: '8px', 
                                    bgcolor: q.type === 'Satisfaction' ? '#EEF2FF' : q.type === 'Pré-Formation' ? '#F0FDF4' : '#FFF7ED',
                                    color: q.type === 'Satisfaction' ? '#6366F1' : q.type === 'Pré-Formation' ? '#22C55E' : '#F97316',
                                    fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase',
                                    transition: 'all 0.2s',
                                    '&:hover': { opacity: 0.8, boxShadow: '0 0 0 2px rgba(99,102,241,0.2)' }
                                }}>
                                    {q.type}
                                </Box>
                                </Tooltip>
                                )}
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                                    {q.formation_name || (q.formation_id ? 'Formation inconnue' : 'Global (Toutes formations)')}
                                </Typography>
                            </TableCell>
                            <TableCell onDoubleClick={() => q.formation_id ? handleEditStart(q, 'session_id') : null} sx={{ cursor: q.formation_id ? 'pointer' : 'default' }}>
                                {editingCell?.id === q.id && editingCell?.field === 'session_id' ? (
                                <TextField
                                    select
                                    autoFocus
                                    size="small"
                                    value={editValue}
                                    onChange={(e) => {
                                      setEditValue(e.target.value);
                                      handleEditSave(q, 'session_id', e.target.value);
                                    }}
                                    onBlur={() => handleEditSave(q, 'session_id')}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') handleEditCancel();
                                    }}
                                    variant="standard"
                                    sx={{ minWidth: 160 }}
                                    SelectProps={{ native: true }}
                                    disabled={loadingEditSessions}
                                >
                                    <option value="">{loadingEditSessions ? 'Chargement...' : '-- Toutes sessions --'}</option>
                                    {editSessions.map((s) => (
                                      <option key={s.id} value={s.id}>Session N° {s.number}</option>
                                    ))}
                                </TextField>
                                ) : (
                                <Tooltip title={q.formation_id ? 'Double-cliquez pour modifier' : 'Assignez une formation d\'abord'} placement="top" arrow>
                                <Typography variant="caption" sx={{ 
                                    fontWeight: 700, color: '#64748B',
                                    transition: 'all 0.2s',
                                    '&:hover': q.formation_id ? { color: '#6366F1' } : {}
                                }}>
                                    {q.session_name ? `S${q.session_name}` : (q.formation_id ? 'Toutes sessions' : '-')}
                                </Typography>
                                </Tooltip>
                                )}
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                <Tooltip title="Aperçu">
                                    <IconButton size="small" onClick={() => handleOpenPreview(q)} sx={{ color: '#64748B' }}>
                                    <EyeIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Envoyer">
                                    <IconButton size="small" onClick={() => q.formation_id ? handleSendEmail(q.id) : handleOpenSendModal(q.id)} sx={{ color: '#0F172A' }}>
                                    <PaperAirplaneIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Dupliquer">
                                    <IconButton size="small" onClick={() => handleDuplicate(q.id)} sx={{ color: '#6366F1' }}>
                                    <DuplicateIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Éditer">
                                    <IconButton size="small" component={RouterLink} to={`/dashboard/questionnaire-formation/${q.id}`} sx={{ color: '#4F46E5' }}>
                                    <PencilAltIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                    <IconButton size="small" onClick={() => handleDelete(q.id)} sx={{ color: '#EF4444' }}>
                                    <TrashIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                </Box>
                            </TableCell>
                            </TableRow>
                        ))}
                    </AnimatePresence>
                    {filteredQuestionnaires.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                          <Box sx={{ opacity: 0.5 }}>
                            <SearchIcon sx={{ fontSize: 48, mb: 2, color: '#94A3B8' }} />
                            <Typography variant="h6" sx={{ color: '#94A3B8', fontWeight: 800 }}>Aucun résultat</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Scrollbar>
          </Card>
        </Container>

        {/* --- DIALOGS (Kept functionality, updated style) --- */}
        <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 900, color: '#0F172A' }}>Créer un nouveau questionnaire</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <TextField fullWidth label="Nom du questionnaire" variant="outlined" margin="normal" value={nom} onChange={(e) => setNom(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }} />
              <TextField select fullWidth label="Type du questionnaire" variant="outlined" margin="normal" value={type} onChange={(e) => setType(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }}>
                <MenuItem value="Pré-Formation">Pré-Formation</MenuItem>
                <MenuItem value="Post-Formation">Post-Formation</MenuItem>
                <MenuItem value="Satisfaction">Questionnaire de satisfaction</MenuItem>
              </TextField>
              <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', p: 2, bgcolor: '#F8FAFC', borderRadius: '12px' }}>
                <Typography variant="body2" sx={{ mr: 2, fontWeight: 700, color: '#475569' }}>Est-il global (toutes formations) ?</Typography>
                <TextField select size="small" value={isGlobal ? 'yes' : 'no'} onChange={(e) => setIsGlobal(e.target.value === 'yes')} variant="outlined" sx={{ width: 100 }} SelectProps={{ native: true }} InputProps={{ sx: { borderRadius: '8px' } }}>
                    <option value="no">Non</option>
                    <option value="yes">Oui</option>
                </TextField>
              </Box>
              {!isGlobal && (
                <>
                  <TextField select fullWidth label="Sélectionner une formation" variant="outlined" margin="normal" value={selectedFormation} onChange={(e) => setSelectedFormation(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }}>
                    <MenuItem value="">-- Choisir une formation --</MenuItem>
                    {formations.map((f) => ( <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem> ))}
                  </TextField>
                  {selectedFormation && (
                    <TextField select fullWidth label="Sélectionner une session (Optionnel)" variant="outlined" margin="normal" value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} InputProps={{ sx: { borderRadius: '12px' } }}>
                      <MenuItem value="">-- Toutes les sessions --</MenuItem>
                      {sessions.map((s) => ( <MenuItem key={s.id} value={s.id}>Session N° {s.number}</MenuItem> ))}
                    </TextField>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose} sx={{ color: '#64748B', fontWeight: 800 }}>Annuler</Button>
            <Button onClick={handleCreate} color="primary" variant="contained" disabled={isSubmitting} sx={{ borderRadius: '12px', px: 4, fontWeight: 800 }}>Créer</Button>
          </DialogActions>
        </Dialog>

        {/* Modal d'envoi contextuel */}
        <Dialog open={openSendModal} onClose={() => setOpenSendModal(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
          <DialogTitle sx={{ fontWeight: 900 }}>Envoi contextuel</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Veuillez cibler la formation et session pour l'envoi de ce questionnaire global.</Typography>
            <TextField select fullWidth label="Formation" variant="outlined" margin="normal" value={sendFormation} onChange={async (e) => {
                const fId = e.target.value; setSendFormation(fId); setSendSession('');
                if (fId) { const data = await questionnaireApi.getSessionsByFormationId(fId, localStorage.getItem('accessToken')); setSendSessions(data || []); }
                else setSendSessions([]);
              }} InputProps={{ sx: { borderRadius: '12px' } }}>
              <MenuItem value="">-- Choisir une formation --</MenuItem>
              {formations.map((f) => ( <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem> ))}
            </TextField>
            <TextField select fullWidth label="Session" variant="outlined" margin="normal" value={sendSession} onChange={(e) => setSendSession(e.target.value)} disabled={!sendFormation} InputProps={{ sx: { borderRadius: '12px' } }}>
              <MenuItem value="">-- Choisir une session --</MenuItem>
              {sendSessions.map((s) => ( <MenuItem key={s.id} value={s.id}>Session N° {s.number}</MenuItem> ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenSendModal(false)} sx={{ fontWeight: 800 }}>Annuler</Button>
            <Button variant="contained" color="primary" disabled={!sendSession} onClick={() => handleSendEmail(sendingQuestionnaireId, sendFormation, sendSession)} sx={{ borderRadius: '12px', fontWeight: 800 }}>Envoyer</Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Preview */}
        <Dialog open={openPreview} onClose={() => setOpenPreview(false)} fullWidth maxWidth="md" scroll="body" PaperProps={{ sx: { borderRadius: '24px' } }}>
          <DialogTitle sx={{ backgroundColor: '#f0ebf8', pb: 0, pt: 2 }}>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Aperçu du questionnaire</Typography>
                <Button onClick={() => setOpenPreview(false)} sx={{ color: '#64748B' }}>Fermer</Button>
             </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, backgroundColor: '#f0ebf8' }}>
            {loadingPreview ? ( <Box sx={{ p: 4, textAlign: 'center' }}>Chargement...</Box> ) : (
              <QuestionnairePreview questionnaire={previewData} questions={previewQuestions} />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default QuestionnaireFormation;
